/**
 * 계약 이미지 업로드 컴포넌트
 * 계약서와 관련된 이미지 파일을 업로드하고 관리하는 컴포넌트입니다.
 * 드래그 앤 드롭 기능을 지원하며, 이미지 미리보기와 PDF 뷰어를 제공합니다.
 */

import { useAuth0 } from "@auth0/auth0-react";
import { AxiosInstance, AxiosResponse } from "axios";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { uploadFilesAsync } from "src/api/file/file-api";
import { useApiOperation } from "src/api/hooks";
import { SaveMediaDto } from "src/api/organization/org-types";
import { ApiResponse, Modal } from "src/api/public-types";
import onErrorImage from "src/assets/images/onerror_preview.svg";
import { BaseButton, BaseModal } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { useModal } from "src/recoil/modalState/hook";

type Props = {
  isOnlyView?: boolean; // 상세 보기 모드 여부
  urlKey?: string; // URL로 사용하는 키 명칭
  images?: SaveMediaDto[]; // 부모 컴포넌트에 저장된 이미지 배열
  setImages?: (files: SaveMediaDto[]) => void; // 이미지 상태 업데이트 함수
  className?: string; // 스타일 클래스
  categoryName?: string; // 이미지 업데이트 컴포넌트 구분용 라디오 버튼 이름
  fileMaxLength?: number; // 최대 파일 개수
  accept?: string; // 허용되는 파일 확장자
  type: "LOGO" | "PROOF"; // 이미지 타입
  serviceTag?: string; // 서비스 태그
  disabled?: boolean; // 비활성화 여부
};

/**
 * S3 기본 URL 설정 (v1.11 개선사항 적용 24.3.8)
 */
const s3Url = process.env.REACT_APP_S3_BASEURL;

export async function getFileDownload(axios: AxiosInstance, param: any): Promise<AxiosResponse<ApiResponse<any>>> {
  const response = await axios.get<ApiResponse<any>>(param?.url, { responseType: "blob" });
  return response;
}

//TODO 이용신청서 파일 업로드 > 계약탭 > 계약서 ContractImageUpload 파일 복사한것 > 추후 ContractImageUpload 수정 필요 (파일 삭제 관련)
const UsageApplicationFileUpload = memo(({ setImages, images, serviceTag, type, fileMaxLength = 10, isOnlyView, accept, disabled }: Props) => {
  // 상태 관리

  // const [previewImage, setPreviewImage] = useState<SaveMediaDto[]>([...(images || [])]); // 미리보기 이미지
  const { isAuthority } = usePartnerAuthority();
  const fileRef = useRef<HTMLInputElement>(null); // 파일 입력 참조
  const { executeAsync: postImageFile } = useApiOperation(uploadFilesAsync, { noLoading: true }); // 이미지 업로드 API
  const [modalOpen, setModalOpen] = useState<Modal>({ isOpen: false }); // 모달 상태
  const [isFullScreenImageOpen, setIsFullScreenImageOpen] = useState(false); // 전체화면 모드
  const [pageNumber, setPageNumber] = useState<number>(1); // PDF 페이지 번호
  const [numPages, setNumPages] = useState<number>(0); // PDF 총 페이지 수
  const [focusFullScreenImageIndex, setFocusFullScreenImageIndex] = useState(0); // 전체화면 이미지 인덱스
  const { executeAsync: getFile } = useApiOperation(getFileDownload, { noAuthenticationRequired: true, noErrorModal: true });
  const { getIdTokenClaims, getAccessTokenSilently } = useAuth0();
  const { setBaseModal } = useModal();

  const [attachments, setAttachments] = useState<SaveMediaDto[]>([...(images || [])]);

  // PDF 워커 설정
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

  // 파일 업로드 제한 설정
  const maxSize = 10;
  const fileValidationText = `한 파일당 업로드 용량은 ${maxSize}MB, 최대 ${fileMaxLength}개까지만 가능합니다.`;

  const [token, setToken] = useState("");

  const getToken = async () => {
    await getAccessTokenSilently();
    const response = await getIdTokenClaims();
    const idToken = response?.__raw;
    setToken(idToken!);
  };
  useEffect(() => {
    if (token === "") {
      getToken();
    }
  }, [token]);

  const isFileValidatiaon = (fileArray: FileList) => {
    const sizeLimit = 10 * 1024 * 1024;
    let validationStatus = true;
    if (fileArray) {
      // 파일 크기 검사
      for (let i = 0; i < fileArray.length; i++) {
        if (fileArray[i].size > sizeLimit) {
          setBaseModal({
            isOpen: true,
            title: "첨부파일 업로드가 실패했습니다.",
            children: <p>{fileValidationText}</p>,
            btnRightTitle: "확인",
            onClick: () => setBaseModal({ isOpen: false }),
          });
          validationStatus = false;
          break;
        }
      }
      // 파일 개수 검사
      const inputCount = fileArray.length;
      const sum = inputCount + (attachments.length || 0);
      if (fileMaxLength && fileMaxLength < sum) {
        setModalOpen({ isOpen: true, message: fileValidationText });
        validationStatus = false;
      }
    }
    return validationStatus;
  };

  const onChangeFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files!;

    if (!isFileValidatiaon(files)) return;

    let s3Array: Array<any> = [];

    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        let file = files[i];
        const formData = new FormData();
        formData.append("file", file);

        const res = await postImageFile({ file: formData, type: type === "PROOF" ? "private" : "public" }).catch((error: unknown) => {
          console.error("Error uploading file:", error);
          return undefined;
        });

        if (res) {
          const data = JSON.parse(JSON.stringify({ ...res.data.data.media, ...{ type, serviceTag, isDeleted: false } }));

          /**
           * 파일을 Data URL로 변환
           * @param file 변환할 파일
           * @returns Promise<string> Data URL
           */
          const readAsDataURL = (file: Blob) => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve(reader.result);
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
          };

          try {
            const blobUrl = await readAsDataURL(file);
            s3Array.push({ ...data, blobUrl });
          } catch (error) {
            console.error("Error reading file:", error);
          }
        }

        const newArra: any = [...s3Array, ...attachments];
        if (setImages) setImages(newArra);
        setAttachments(newArra);
      }
    }
    event.target.value = "";
  };

  /**
   * 이미지 삭제
   * 선택된 이미지를 삭제하거나 삭제 표시합니다.
   * @param passFile 삭제할 파일
   * @param e 이벤트 객체
   */
  const onImageClear = (passFile: any, e: any) => {
    e.stopPropagation();
    let newArr = [];

    if (!passFile.id) {
      newArr = attachments.filter((item: any) => item.key !== passFile.key) || [];
    } else {
      newArr =
        attachments.map((file: any) => {
          if (file.id === passFile.id) {
            file = { ...file, isRemoved: true, isDeleted: true, cmdType: "D" };
          }
          return file;
        }) || [];
    }
    setAttachments(newArr);
    if (setImages) setImages(newArr);
  };

  /**
   * 전체화면 이미지 열기
   * @param idx 이미지 인덱스
   */
  const handleOpenFullImage = (idx: number) => {
    setFocusFullScreenImageIndex(idx);
    setIsFullScreenImageOpen(true);
  };

  /**
   * 전체화면 이미지 이동
   * @param position 이동 방향 ('left' 또는 'right')
   */
  const handleFullScreenImage = (position: "left" | "right") => {
    if (position === "left") {
      setFocusFullScreenImageIndex((prev) => (prev === 0 ? (attachments.length || 0) - 1 : prev - 1));
    } else {
      setFocusFullScreenImageIndex((prev) => (prev === (attachments.length || 0) - 1 ? 0 : prev + 1));
    }
  };

  /**
   * 이미지 URL 찾기
   * @param file 파일 객체
   * @returns 이미지 URL
   */
  const findImageURL = (file: any) => {
    if (file.blobUrl) {
      return file.blobUrl;
    }
    if (file.url && token) {
      return file.url + "?access_token=" + token;
    }

    return "";
  };

  /**
   * PDF 문서 로드 성공 핸들러
   * @param numPages PDF 총 페이지 수
   */
  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
  }

  /**
   * PDF 이전 페이지로 이동
   */
  const handlePdfLeft = () => {
    setPageNumber((prev) => (prev === 1 ? numPages : prev - 1));
  };

  /**
   * PDF 다음 페이지로 이동
   */
  const handlePdfRight = () => {
    setPageNumber((prev) => (prev === numPages ? 1 : prev + 1));
  };

  const renderFileView = (file: any) => {
    const fileUrlWithToken = file.blobUrl
      ? file.blobUrl
      : file.url
      ? file.url + "?access_token=" + token
      : s3Url + file.key + "?access_token=" + token;
    const fileName = file.filename;
    const fileExtension = file.filename.slice(file.filename.lastIndexOf(".") + 1);
    const availableFileExtensionArray = ["jpg", "jpeg", "gif", "png", "svg", "bmp"];

    // 이미지
    if (availableFileExtensionArray?.includes(fileExtension.toLowerCase())) {
      return (
        <>
          <img
            src={findImageURL(file)}
            alt={fileName || "이미지를 불러오는데 실패했습니다."}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null; // prevents looping
              currentTarget.src = onErrorImage;
            }}
          />
          <p className="file-name">{fileName}</p>
        </>
      );
    }

    // 이미지가 아닐경우
    switch (fileExtension) {
      case "pdf":
        return (
          <div className="file-viewer-pdf">
            {token && (
              <Document
                className="file-viewer-pdf__document"
                file={fileUrlWithToken ? fileUrlWithToken : ""}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={console.error}
              >
                <Page pageNumber={pageNumber} renderAnnotationLayer={false} loading={"loading..."} onLoadError={console.error} />
              </Document>
            )}
            <div className="button-wrap">
              <button className="left" disabled={pageNumber === 1} onClick={handlePdfLeft}></button>
              <button className="right" disabled={pageNumber === numPages} onClick={handlePdfRight}></button>
            </div>
          </div>
        );
      default:
        return (
          <div className="no-preview-available">
            <p>미리보기를 제공하지 않는 파일 형식입니다.</p>
            <p>다운로드 후 확인해 주세요.</p>
            <p className="file-name">{fileName}</p>
          </div>
        );
    }
  };

  useEffect(() => {
    const escapeKeyDownHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter") {
        e.preventDefault();
        setIsFullScreenImageOpen(false);
      }
    };

    window.addEventListener("keydown", escapeKeyDownHandler);

    return () => {
      window.removeEventListener("keydown", escapeKeyDownHandler);
    };
  }, []);

  // response의 headers에 있는 content-disposition을 찾아서 디코딩 한다.
  const forceDownload = useCallback(
    async (url: string, filename: string) => {
      let gf: any;
      try {
        gf = await getFile({ url });
      } catch (error) {
        console.error("Error downloading file:", error);
        return;
      }

      const blob = new Blob([gf.data]);
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.style.display = "none";

      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(fileUrl);
    },
    [getFile],
  );

  const sliceTitle = (fileName: string) => {
    const dotIndex = fileName.lastIndexOf(".");
    const title = fileName.slice(0, dotIndex);
    const extension = fileName.slice(dotIndex + 1);
    return { title, extension };
  };

  return (
    <div className="base-image-wrap minmax380">
      {!isOnlyView && (
        <>
          {isAuthority("w", "BASIC") ? (
            <>
              <input
                ref={fileRef}
                type="file"
                style={{ display: "none" }}
                accept={accept ? accept : "*"}
                onChange={onChangeFile}
                // onChange={onSaveImages}
                multiple
              />
              <BaseButton
                title={"파일찾기"}
                className="color-white"
                disabled={(fileMaxLength && attachments.length >= fileMaxLength ? true : false) || disabled === true}
                onClick={() => fileRef.current?.click()}
              />
              {attachments.length === 0 && (
                <ul className="base-list mt10">
                  <li>문서를 카메라로 찍거나 스캔하여 업로드해주세요.</li>
                  <li>업로드 용량은 개당 10MB까지 가능합니다.</li>
                </ul>
              )}
            </>
          ) : (
            attachments.length === 0 && <p className="font14">-</p>
          )}
        </>
      )}
      {attachments && attachments.length > 0 ? (
        <div className="base-image ">
          {attachments
            .filter((image) => image.cmdType !== "D")
            .map((image, idx) => {
              return (
                <div
                  className={`base-image__wrap ${isOnlyView ? "detail" : "form"}`}
                  key={idx}
                  draggable={true}
                  data-position={idx}
                  onClick={() => handleOpenFullImage(idx)}
                >
                  <img
                    src={findImageURL(image)}
                    alt="preview-images"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null; // prevents looping
                      currentTarget.src = onErrorImage;
                    }}
                  />
                  {!isOnlyView && <button type="button" onClick={(e) => onImageClear(image, e)} />}

                  <div className="title-area">
                    <div className="not-hover">
                      <p>{sliceTitle(image.filename).title}</p>
                      <span>.{sliceTitle(image.filename).extension}</span>
                    </div>
                    <div className="active-hover">
                      <p>{sliceTitle(image.filename).title}</p>
                      <span>.{sliceTitle(image.filename).extension}</span>
                      {isOnlyView && (
                        <button
                          type="button"
                          className="detail-download"
                          onClick={(e) => {
                            e.stopPropagation();
                            forceDownload(image.url! + "?access_token=" + token, image.filename);
                          }}
                        ></button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          {isFullScreenImageOpen && (
            <BaseAbstractModal isNotUseWhiteBoard isOpen={true} opacity={0.75}>
              <div className="base-image__full-screen">
                {renderFileView(attachments[focusFullScreenImageIndex])}
                <div className="img-button-wrap">
                  <div className="flicker">
                    <button className="left" onClick={() => handleFullScreenImage("left")}></button>
                    <p>
                      {focusFullScreenImageIndex + 1} / {attachments.length}
                    </p>
                    <button className="right" onClick={() => handleFullScreenImage("right")}></button>
                  </div>
                  <a
                    target="_self"
                    className="download"
                    href={" "}
                    rel="noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      forceDownload(
                        attachments[focusFullScreenImageIndex].url + "?access_token=" + token + "&forceDownload",
                        attachments[focusFullScreenImageIndex].filename,
                      );
                    }}
                  >
                    {""}
                  </a>
                  <button className="close" onClick={() => setIsFullScreenImageOpen(false)}>
                    닫기
                  </button>
                </div>
              </div>
            </BaseAbstractModal>
          )}

          {modalOpen.isOpen && (
            <BaseModal isOpen={true} title={`첨부파일 업로드가 실패했습니다.`} btnRightTitle="확인" onClick={() => setModalOpen({ isOpen: false })}>
              <p>{modalOpen.message}</p>
            </BaseModal>
          )}
        </div>
      ) : (
        isOnlyView && <p>-</p>
      )}
    </div>
  );
});

export default React.memo(UsageApplicationFileUpload);
